import {
  CreateQuantificationCheckBy,
  IdMappingErrorMessageType,
  ParentExhaustion,
  SampleTrackingCheckBy,
  SampleTrackingIdMapping,
  SampleTrackingQuantificationCheckByDto,
  SampleTrackingQuantificationDetailsCheckByDto,
} from '../../data/SampleTrackingData';
import {
  checkByFailedReason,
  concentration,
  containerIdentifier,
  containerSamplePosition,
  containerType,
  labAssignedSampleId,
  checkByStatus,
  parentContainerIdentifier,
  parentContainerPosition,
  parentContainerType,
  r1FastQLocation,
  r2FastQLocation,
  sampleIdentifier,
  twoSixtyTwoEightyRatio,
  volume,
  yieldField,
  volumeIsNan,
  yieldIsNan,
  concentrationIsNan,
  twoSixtyTwoEightyIsNan,
  qcStatus,
} from '../../util/Constants';
import { Dictionary } from 'lodash';
import { CheckByFailedReason } from '../../data/ReferenceData';
import { v4 as uuid } from 'uuid';
import { ConfiguredTransition } from '../../data/ConfiguredTransitionData';

type CustomHandleFunc = (
  researchProjectId: string,
  configuredTransitionId: string,
  idMappings: ReadonlyArray<SampleTrackingCheckBy>,
  parentExhaustion: ParentExhaustion,
  accessToken: string
) => Promise<string>;

type GetErrorMessagesFunc = (
  row: SampleTrackingIdMapping,
  config: CheckByConfiguration,
  checkByFailedReasons: ReadonlyArray<CheckByFailedReason>
) => IdMappingErrorMessageType[];

type ConfigurationType = 'Normal' | 'Sequencing' | 'Quantification';

export interface CheckByConfiguration {
  inputTextKey: string;
  maxParsedValues: number;
  rowBuilder: (row: string[], index: number, config: CheckByConfiguration) => SampleTrackingIdMapping;
  requireLabAssignedId: boolean;
  configurationType: ConfigurationType;
  supportsCheckByFailedReason: boolean;
  columns?: string[];
  customHandle?: CustomHandleFunc;
  getErrorMessages?: GetErrorMessagesFunc;
}

export function BuildCheckByConfiguration(
  configuredTransition: ConfiguredTransition,
  t: (val: string) => string
): CheckByConfiguration {
  let configurationType: ConfigurationType = 'Normal';
  const columns = [sampleIdentifier];

  if (
    ['supportsOutputSamples', 'requiresOutputSamples', 'relabels'].includes(configuredTransition.sampleActionOnCheckBy)
  ) {
    columns.push(labAssignedSampleId);
  }

  columns.push(checkByStatus);

  if (configuredTransition.enableCheckByFailedReason) {
    columns.push(checkByFailedReason);
  }

  if (
    ['supportsOutputSamples', 'requiresOutputSamples'].includes(configuredTransition.sampleActionOnCheckBy) &&
    configuredTransition.transitionEnum?.name !== 'Sequencing'
  ) {
    columns.push(containerIdentifier);
    columns.push(containerSamplePosition);
    columns.push(containerType);
  }

  if (configuredTransition.transitionEnum?.name === 'Sequencing') {
    columns.push(r1FastQLocation, r2FastQLocation);
    configurationType = 'Sequencing';
  } else if (configuredTransition.transitionEnum?.name === 'Quantification') {
    columns.push(
      volume,
      yieldField,
      concentration,
      twoSixtyTwoEightyRatio,
      parentContainerIdentifier,
      parentContainerPosition,
      parentContainerType
    );
    configurationType = 'Quantification';
  }

  columns.push(qcStatus);

  let requireLabAssignedId = false;

  if (['requiresOutputSamples'].includes(configuredTransition.sampleActionOnCheckBy)) {
    requireLabAssignedId = true;
  }

  let customHandleFunc: CustomHandleFunc | undefined = undefined;
  let getErrorMessagesFunc: GetErrorMessagesFunc | undefined = undefined;

  if (configuredTransition.transitionEnum?.name === 'Quantification') {
    customHandleFunc = async (
      researchProjectId: string,
      configuredTransitionId: string,
      idMappings: ReadonlyArray<SampleTrackingCheckBy>,
      parentExhaustion: ParentExhaustion,
      accessToken: string
    ) => {
      const payload: SampleTrackingQuantificationCheckByDto = {
        sampleTrackingCheckByDtos: idMappings,
        sampleTrackingQuantificationDetailsCheckByDtos: idMappings.map(i => {
          i.lookupId = uuid();

          let dto: SampleTrackingQuantificationDetailsCheckByDto = {
            sampleId: i.originalSampleId,
          };

          if (i.dynamicData !== undefined) {
            dto.lookupId = i.lookupId;
            dto.volume = i.dynamicData[volume] ? +i.dynamicData[volume] : undefined;
            dto.yield = i.dynamicData[yieldField] ? +i.dynamicData[yieldField] : undefined;
            dto.concentration = i.dynamicData[concentration] ? +i.dynamicData[concentration] : undefined;
            dto.twoSixtyTwoEightyRatio = i.dynamicData[twoSixtyTwoEightyRatio]
              ? +i.dynamicData[twoSixtyTwoEightyRatio]
              : undefined;
            dto.parentContainerIdentifier = i.dynamicData[parentContainerIdentifier]
              ? i.dynamicData[parentContainerIdentifier]
              : undefined;
            dto.parentContainerPosition = i.dynamicData[parentContainerPosition]
              ? i.dynamicData[parentContainerPosition]
              : undefined;
            dto.parentContainerType = i.dynamicData[parentContainerType]
              ? i.dynamicData[parentContainerType]
              : undefined;
          }

          return dto;
        }),
      };

      return await CreateQuantificationCheckBy(researchProjectId, configuredTransitionId, payload, accessToken);
    };

    getErrorMessagesFunc = (row: SampleTrackingIdMapping) => {
      if (row.dynamicData === undefined) {
        return [];
      }

      const errorMessages: IdMappingErrorMessageType[] = [];

      const volumeValue = row.dynamicData[volume];
      const yieldValue = row.dynamicData[yieldField];
      const concentrationValue = row.dynamicData[concentration];
      const twoSixtyTwoEightyRatioValue = row.dynamicData[twoSixtyTwoEightyRatio];

      if (volumeValue && isNaN(+volumeValue)) {
        errorMessages.push(volumeIsNan);
      }

      if (yieldValue && isNaN(+yieldValue)) {
        errorMessages.push(yieldIsNan);
      }

      if (concentrationValue && isNaN(+concentrationValue)) {
        errorMessages.push(concentrationIsNan);
      }

      if (twoSixtyTwoEightyRatioValue && isNaN(+twoSixtyTwoEightyRatioValue)) {
        errorMessages.push(twoSixtyTwoEightyIsNan);
      }

      return errorMessages;
    };
  }

  return {
    supportsCheckByFailedReason: configuredTransition.enableCheckByFailedReason,
    configurationType: configurationType,
    requireLabAssignedId: requireLabAssignedId,
    inputTextKey: columns.map(i => t(i)).join(','),
    maxParsedValues: columns.length,
    rowBuilder: (row: string[], index: number, config: CheckByConfiguration): SampleTrackingIdMapping => {
      const dynamicData: Dictionary<string | undefined> = {};

      config.columns?.forEach(
        (column, index) => (dynamicData[column] = row.length > index ? row[index] || undefined : undefined)
      );

      return {
        index: index.toString(),
        sampleIdentifier: '',
        checkByStatus: '',
        ...dynamicData,
        dynamicData: dynamicData,
      };
    },
    columns: columns,
    customHandle: customHandleFunc,
    getErrorMessages: getErrorMessagesFunc,
  };
}
